<template>
  <div class="phone-list-page">
    <Edit
      :editDialog="editDialog"
      @close="editDialog = false"
      @submit="productEditSubmit"
      :id="selectedProduct"
    />

    <v-card :loading="loading" outlined class="phone-list-card">
      <div class="phone-list-header">
        <div>
          <div class="phone-list-title">Products</div>
          <div class="phone-list-subtitle">
            {{ products.length }} products found
          </div>
        </div>

        <div class="phone-list-search-wrap">
          <div class="filter-label">Search Products</div>
          <v-text-field
            v-model="search"
            outlined
            prepend-inner-icon="search"
            dense
            label="Search by product name"
            single-line
            hide-details
            clearable
            class="phone-list-search"
          />
        </div>
      </div>

      <v-data-table
        :mobile-breakpoint="0"
        :headers="headers"
        :items="products"
        :search="search"
        :loading="loading"
        :sort-by="sortBy"
        :page.sync="page"
        :single-expand="singleExpand"
        :expanded.sync="expanded"
        show-expand
        :items-per-page="itemsPerPage"
        :footer-props="{ 'items-per-page-options': [10, 15, 25, 50, 100] }"
      >
        <template v-slot:item="{ item, isExpanded, expand }">
          <tr class="pointer">
            <td class="text-left">
              <v-btn
                v-if="item.balance && item.trackSerialNumber"
                @click="expand(!isExpanded)"
                icon
              >
                <v-icon>
                  {{ isExpanded ? "mdi-chevron-up" : "mdi-chevron-down" }}
                </v-icon>
              </v-btn>
            </td>

            <td class="text-left">
              <div class="product-name">
                {{ item.name }}

                <v-chip
                  v-if="item.trackSerialNumber"
                  x-small
                  color="blue lighten-5"
                  text-color="blue darken-3"
                  label
                  class="ml-2"
                >
                  <v-icon left x-small>mdi-barcode-scan</v-icon>
                  Serial
                </v-chip>

                <v-chip
                  v-else-if="item.trackInventory"
                  x-small
                  color="teal lighten-5"
                  text-color="teal darken-3"
                  label
                  class="ml-2"
                >
                  <v-icon left x-small>mdi-package-variant-closed</v-icon>
                  Inventory
                </v-chip>
              </div>
            </td>

            <td class="text-center">
              <v-chip
                small
                label
                :color="Number(item.balance || 0) <= 0 ? 'red lighten-5' : 'green lighten-5'"
                :text-color="Number(item.balance || 0) <= 0 ? 'red darken-3' : 'green darken-3'"
              >
                {{ item.balance || 0 }}
              </v-chip>
            </td>

            <td class="text-right">
              {{ item.price | currency }}
            </td>

            <td class="justify-end align-center text-right">
              <v-menu bottom left offset-y>
                <template v-slot:activator="{ on }">
                  <v-btn v-on="on" icon>
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>

                <v-list dense class="menu-button">
                  <v-list-item @click="editProduct(item)">
                    <v-list-item-title>
                      {{ $t("buttons.edit") }}
                    </v-list-item-title>
                  </v-list-item>

                  <v-list-item @click="deleteProduct(item)">
                    <v-list-item-title>
                      {{ $t("buttons.delete") }}
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </td>
          </tr>
        </template>

        <template v-slot:expanded-item="{ headers, item }">
          <td :colspan="headers.length" class="pa-0">
            <v-card color="#f8fafc" flat tile>
              <v-card-text class="pa-3">
                <PurchaseDetialList :ProductId="item.id" />
              </v-card-text>
            </v-card>
          </td>
        </template>

        <v-alert slot="no-results" :value="true" color="error" icon="warning">
          Your search for "{{ search }}" found no results.
        </v-alert>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import productService from "../service";
import PurchaseDetialList from "@/modules/Inventory/Purchase/components/PurchaseDetailList";

const Edit = () => import("./Edit");

export default {
  name: "phone-list",

  props: ["CategoryId", "ProductId"],

  components: {
    Edit,
    PurchaseDetialList,
  },

  data() {
    return {
      expanded: [],
      singleExpand: true,
      editDialog: false,
      products: [],
      selectedProduct: null,
      headers: [
        { text: "", value: "data-table-expand" },
        {
          text: this.$t("labels.name"),
          align: "left",
          value: "name",
          sortable: true,
        },
        {
          text: this.$t("labels.stock"),
          value: "balance",
          align: "center",
          sortable: true,
        },
        {
          text: this.$t("labels.amount"),
          value: "price",
          align: "right",
          sortable: true,
        },
        {
          text: "",
          value: "action",
          sortable: false,
        },
      ],
      search: "",
      loading: true,
      page: 1,
      sortBy: "balance",
      pageCount: 0,
      itemsPerPage: 10,
    };
  },

  created() {
    this.getList();
  },

  methods: {
    productEditSubmit() {
      this.editDialog = false;
      this.getList();
    },

    getList() {
      this.loading = true;

      if (this.CategoryId) {
        return productService
          .getAllCaregoryProduct({ CategoryId: this.CategoryId })
          .then((response) => {
            this.products = response.data || [];
            return response;
          })
          .finally(() => {
            this.loading = false;
          });
      }

      if (this.ProductId) {
        return productService
          .getAll({ ChildProductId: this.ProductId })
          .then((response) => {
            this.products = response.data || [];
            return response;
          })
          .finally(() => {
            this.loading = false;
          });
      }

      this.products = [];
      this.loading = false;
    },

    editProduct(product) {
      this.selectedProduct = product.id;
      this.editDialog = true;
    },

    deleteProduct(product) {
      this.$swal({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      }).then((result) => {
        if (result.value) {
          productService.delete(product.id).then((response) => {
            if (response.data) {
              this.$swal("Deleted!", "Product has been deleted.", "success");
              this.getList();
            }
          });
        }
      });
    },
  },
};
</script>

<style scoped>
.phone-list-page {
  width: 100%;
}

.phone-list-card {
  border-radius: 0;
  background: #ffffff;
  overflow: hidden;
  border-left: 0 !important;
  border-right: 0 !important;
}

.phone-list-header {
  padding: 14px 16px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.phone-list-title {
  font-size: 18px;
  font-weight: 800;
  color: #0f172a;
}

.phone-list-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}

.phone-list-search-wrap {
  min-width: 300px;
}

.filter-label {
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 5px;
}

.phone-list-search {
  max-width: 340px;
}

.product-name {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.pointer:hover {
  background: #f8fafc;
}

@media (max-width: 600px) {
  .phone-list-search-wrap,
  .phone-list-search {
    min-width: 100%;
    max-width: 100%;
  }
}
</style>